import { venueMarketingPageListDtoSchema } from '@lib/dto/venue-marketing-page.dto'
import {
  venueMarketingContentSchema,
  type VenueMarketingContent,
} from '@lib/dto/venue-marketing.dto'
import type { VenueMarketingPageDto } from '@lib/dto/venue-marketing-page.dto'
import { api } from '@/helpers/apiClient'
import { logRequestFailure } from '@/helpers/request-outcome'
import type { IMarketingService } from '@/types/MarketingService.types'
import type { IMarketingImageService } from '@/types/MarketingImageService.types'
import {
  marketingImageDtoSchema,
  marketingImageListDtoSchema,
  type MarketingImageDto,
} from '@lib/dto/marketing-image.dto'

/**
 * A venue's marketing content.
 *
 * The same documents the public pricing sites read, edited here by the people
 * who own them. The content API serves them read-only; everything that changes
 * one comes through these routes, as a person, so the page records who edited it.
 */
class MarketingServiceImpl implements IMarketingService {
  async list(venueId: string): Promise<VenueMarketingPageDto[]> {
    try {
      return await api.get(
        `/venues/${encodeURIComponent(venueId)}/marketing`,
        venueMarketingPageListDtoSchema,
      )
    } catch (cause) {
      logRequestFailure('Failed to load marketing pages', { venueId }, cause)
      throw cause
    }
  }

  async save(
    venueId: string,
    eventTrackId: string | null,
    content: VenueMarketingContent,
  ): Promise<VenueMarketingContent> {
    const base = `/venues/${encodeURIComponent(venueId)}/marketing`
    const path =
      eventTrackId === null ? base : `${base}/tracks/${encodeURIComponent(eventTrackId)}`
    try {
      return await api.put(path, content, venueMarketingContentSchema)
    } catch (cause) {
      logRequestFailure('Failed to save a marketing page', { venueId, eventTrackId }, cause)
      throw cause
    }
  }
}

export const MarketingService: IMarketingService = new MarketingServiceImpl()

/**
 * The images behind a venue's marketing pages.
 *
 * Separate from `MarketingService` because it is a different resource with a
 * different lifetime: an image outlives the field that first referenced it, and
 * is meant to be reused across sections rather than uploaded again.
 */
class MarketingImageServiceImpl implements IMarketingImageService {
  async list(venueId: string): Promise<MarketingImageDto[]> {
    try {
      return await api.get(
        `/venues/${encodeURIComponent(venueId)}/marketing/images`,
        marketingImageListDtoSchema,
      )
    } catch (cause) {
      logRequestFailure('Failed to list marketing images', { venueId }, cause)
      throw cause
    }
  }

  async upload(venueId: string, dataUrl: string): Promise<MarketingImageDto> {
    try {
      return await api.post(
        `/venues/${encodeURIComponent(venueId)}/marketing/images`,
        { dataUrl },
        marketingImageDtoSchema,
      )
    } catch (cause) {
      logRequestFailure('Failed to upload a marketing image', { venueId }, cause)
      throw cause
    }
  }
}

export const MarketingImageService: IMarketingImageService = new MarketingImageServiceImpl()
